import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from './authTypes';
import { authAPI } from './authAPI';

const initialState: AuthState = {
  user: localStorage.getItem('authUser') ? JSON.parse(localStorage.getItem('authUser')!) : null,
  token: localStorage.getItem('authToken'),
  isAuthenticated: !!localStorage.getItem('authToken'),
  loading: false,
  error: null,
  otpSent: false,
  otpPhone: null,
  resetEmailSent: false,
};

const extractErrorMessage = (err: any, defaultMsg: string): string => {
  if (!err) return defaultMsg;

  if (err.data?.error) {
    const errorObj = err.data.error;
    if (typeof errorObj === 'object' && errorObj.message) return errorObj.message;
    if (typeof errorObj === 'string') return errorObj;
  }
  if (err.data?.detail) return err.data.detail;
  if (err.error) {
    if (typeof err.error === 'object' && err.error.message) return err.error.message;
    if (typeof err.error === 'string') return err.error;
  }
  if (err.message) return err.message;
  return defaultMsg;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;

      localStorage.setItem('authToken', action.payload.token);
      localStorage.setItem('authUser', JSON.stringify(action.payload.user));
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.otpSent = false;
      state.otpPhone = null;
      state.resetEmailSent = false;

      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setOTPSent: (state, action: PayloadAction<{ phone: string }>) => {
      state.otpSent = true;
      state.otpPhone = action.payload.phone;
      state.error = null;
    },
    clearOTPState: (state) => {
      state.otpSent = false;
      state.otpPhone = null;
    },
    setResetEmailSent: (state, action: PayloadAction<boolean>) => {
      state.resetEmailSent = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ===== REGISTER =====
    builder.addMatcher(authAPI.endpoints.register.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.register.matchFulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.jwt;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('authToken', action.payload.jwt);
      localStorage.setItem('authUser', JSON.stringify(action.payload.user));
    });
    builder.addMatcher(authAPI.endpoints.register.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Registration failed');
    });

    // ===== LOGIN =====
    builder.addMatcher(authAPI.endpoints.login.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.login.matchFulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.jwt;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('authToken', action.payload.jwt);
      localStorage.setItem('authUser', JSON.stringify(action.payload.user));
    });
    builder.addMatcher(authAPI.endpoints.login.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Login failed');
    });

    // ===== GET CURRENT USER =====
    builder.addMatcher(authAPI.endpoints.getCurrentUser.matchFulfilled, (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.getCurrentUser.matchRejected, (state, action: any) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      state.error = extractErrorMessage(action.payload, 'Session expired');
    });

    // ===== LOGOUT =====
    builder.addMatcher(authAPI.endpoints.logout.matchFulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.otpSent = false;
      state.otpPhone = null;
      state.resetEmailSent = false;
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
    });

    // ===== UPDATE PROFILE =====
    builder.addMatcher(authAPI.endpoints.updateProfile.matchFulfilled, (state, action) => {
      state.user = action.payload;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.updateProfile.matchRejected, (state, action: any) => {
      state.error = extractErrorMessage(action.payload, 'Failed to update profile');
    });

    // ===== OTP REQUEST =====
    builder.addMatcher(authAPI.endpoints.requestOTP.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.requestOTP.matchFulfilled, (state) => {
      state.loading = false;
      state.otpSent = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.requestOTP.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Failed to send OTP');
    });

    // ===== OTP VERIFY =====
    builder.addMatcher(authAPI.endpoints.verifyOTP.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.verifyOTP.matchFulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.jwt;
      state.isAuthenticated = true;
      state.otpSent = false;
      state.otpPhone = null;
      state.error = null;
      localStorage.setItem('authToken', action.payload.jwt);
    });
    builder.addMatcher(authAPI.endpoints.verifyOTP.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Invalid OTP');
    });

    // ===== FORGOT PASSWORD =====
    builder.addMatcher(authAPI.endpoints.forgotPassword.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.forgotPassword.matchFulfilled, (state) => {
      state.loading = false;
      state.resetEmailSent = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.forgotPassword.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Failed to send reset email');
    });

    // ===== RESET PASSWORD =====
    builder.addMatcher(authAPI.endpoints.resetPassword.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.resetPassword.matchFulfilled, (state) => {
      state.loading = false;
      state.resetEmailSent = false;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.resetPassword.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Failed to reset password');
    });

    // ===== CHANGE PASSWORD =====
    builder.addMatcher(authAPI.endpoints.changePassword.matchRejected, (state, action: any) => {
      state.error = extractErrorMessage(action.payload, 'Failed to change password');
    });

    // ===== FIREBASE SOCIAL AUTH =====
    builder.addMatcher(authAPI.endpoints.firebaseSocialAuth.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addMatcher(authAPI.endpoints.firebaseSocialAuth.matchFulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.jwt;
      state.isAuthenticated = true;
      state.error = null;
      localStorage.setItem('authToken', action.payload.jwt);
    });
    builder.addMatcher(authAPI.endpoints.firebaseSocialAuth.matchRejected, (state, action: any) => {
      state.loading = false;
      state.error = extractErrorMessage(action.payload, 'Social authentication failed');
    });
  },
});

export const {
  setCredentials,
  logout,
  clearError,
  updateUser,
  setOTPSent,
  clearOTPState,
  setResetEmailSent,
} = authSlice.actions;

export default authSlice.reducer;
